import {
  useRef,
  useSyncExternalStore,
  type CSSProperties,
  type RefObject,
} from "react";
import { useEditorTabPanelScrollRef } from "@/features/sparxsEditor/contexts/EditorTabPanelScrollContext";

const HIDDEN_OVERLAY_STYLE: CSSProperties = { display: "none" };

export interface EditorTabPanelOverlayState {
  overlayRoot: HTMLElement | null;
  overlayStyle: CSSProperties;
}

const HIDDEN_OVERLAY_STATE: EditorTabPanelOverlayState = {
  overlayRoot: null,
  overlayStyle: HIDDEN_OVERLAY_STYLE,
};

export const useEditorTabPanelOverlayRoot = (
  anchorRef: RefObject<HTMLElement | null> | null,
  enabled: boolean,
): EditorTabPanelOverlayState => {
  const tabPanelRef = useEditorTabPanelScrollRef();
  const snapshotRef = useRef<EditorTabPanelOverlayState>(HIDDEN_OVERLAY_STATE);
  const snapshotKeyRef = useRef("hidden");

  return useSyncExternalStore(
    (onStoreChange) => {
      const scrollRoot = tabPanelRef?.current ?? null;

      if (!enabled || anchorRef == null || scrollRoot == null) {
        return () => undefined;
      }

      const syncOverlay = () => {
        onStoreChange();
      };

      scrollRoot.addEventListener("scroll", syncOverlay, { passive: true });
      window.addEventListener("resize", syncOverlay, { passive: true });
      window.addEventListener("scroll", syncOverlay, { passive: true });

      const resizeObserver = new ResizeObserver(syncOverlay);
      resizeObserver.observe(scrollRoot);

      return () => {
        scrollRoot.removeEventListener("scroll", syncOverlay);
        window.removeEventListener("resize", syncOverlay);
        window.removeEventListener("scroll", syncOverlay);
        resizeObserver.disconnect();
      };
    },
    () => {
      if (!enabled || anchorRef == null) {
        return HIDDEN_OVERLAY_STATE;
      }

      const scrollRoot = tabPanelRef?.current ?? null;
      if (scrollRoot == null) {
        return HIDDEN_OVERLAY_STATE;
      }

      const rect = scrollRoot.getBoundingClientRect();
      const key = `${rect.top}|${rect.left}|${rect.width}|${rect.height}`;
      if (snapshotKeyRef.current === key) {
        return snapshotRef.current;
      }

      snapshotKeyRef.current = key;
      snapshotRef.current = {
        overlayRoot: scrollRoot,
        overlayStyle: {
          position: "fixed",
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          pointerEvents: "none",
          zIndex: 20,
        },
      };
      return snapshotRef.current;
    },
    () => HIDDEN_OVERLAY_STATE,
  );
};
